import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { getCycleReferenceDateOverride, isCycleRolloverPreviewEnabled } from "../utils/cmeCycle";
import { cycleDateString, cycleKey, cycleRangeLabel } from "../utils/cmeCycleHistory";

function dateInputValue(date) {
    if (!date) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function CycleRolloverPreview({ referenceDate, currentCycle, previousCycle }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [testDate, setTestDate] = useState(() => dateInputValue(referenceDate));
    const [error, setError] = useState("");

    useEffect(() => {
        setTestDate(dateInputValue(referenceDate));
        setError("");
    }, [referenceDate]);

    const applyDate = (event) => {
        event.preventDefault();
        const params = new URLSearchParams(searchParams);
        params.delete("cycleYear");
        params.set("cycleDate", testDate);
        if (!getCycleReferenceDateOverride(params.toString())) {
            setError("Enter a valid test date.");
            return;
        }
        params.delete("cycle");
        setError("");
        setSearchParams(params, { replace: true });
    };

    const previewNextCycle = () => {
        if (!currentCycle) return;
        const nextDate = new Date(currentCycle.end);
        nextDate.setDate(nextDate.getDate() + 1);
        const params = new URLSearchParams(searchParams);
        params.delete("cycleYear");
        params.delete("cycle");
        params.set("cycleDate", cycleDateString(nextDate));
        setError("");
        setSearchParams(params, { replace: true });
    };

    const viewPreviousCycle = () => {
        if (!previousCycle) return;
        const params = new URLSearchParams(searchParams);
        params.set("cycle", cycleKey(previousCycle));
        setSearchParams(params, { replace: true });
    };

    const resetDate = () => {
        const params = new URLSearchParams(searchParams);
        params.delete("cycleDate");
        params.delete("cycleYear");
        params.delete("cycle");
        setTestDate("");
        setError("");
        setSearchParams(params, { replace: true });
    };

    if (!isCycleRolloverPreviewEnabled(searchParams.toString())) return null;

    return (
        <Box sx={{ my: 2, p: 2, border: "2px dashed #ff9800", borderRadius: 2, backgroundColor: "#fff3e0" }}>
            <Typography variant="h6" color="#e65100" gutterBottom>
                Dev Mode: Test Cycle Rollover
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a date such as 2027-01-01 to preview this state's cycle dates.
                Progress and previous cycles reflect the test date. Your saved certificates and license information are preserved.
            </Typography>
            <Stack component="form" onSubmit={applyDate} direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
                <TextField
                    type="date"
                    label="Cycle Test Date"
                    value={testDate}
                    onChange={(event) => {
                        setTestDate(event.target.value);
                        setError("");
                    }}
                    error={Boolean(error)}
                    helperText={error || "Test the day before and after a cycle ends."}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button type="submit" variant="contained" sx={{ minHeight: 56 }}>Test date</Button>
                <Button onClick={resetDate} variant="outlined" sx={{ minHeight: 56 }}>Reset to today</Button>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
                <Button onClick={previewNextCycle} variant="outlined" disabled={!currentCycle}>
                    Preview next cycle
                </Button>
                <Button onClick={viewPreviousCycle} variant="outlined" disabled={!previousCycle}>
                    View previous cycle
                </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {previousCycle
                    ? `Latest previous cycle: ${cycleRangeLabel(previousCycle)}. Use View Previous Cycles below to choose another.`
                    : "No previous cycles with saved certificates at this test date. Preview the next cycle to see any current certificates in history."}
                {!currentCycle && " Enter the required cycle details to enable the next-cycle preview."}
            </Typography>
        </Box>
    );
}
